import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DataTableDirective } from 'angular-datatables';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { constants } from 'src/app/core/constants/constants';
import { Criterion, Pagination, SearchItemModel } from 'src/app/core/services/base-event-service';
import {
  ClientSideSearchService,
  FilterRequestItem,
} from 'src/app/core/services/client-side-search.service';
import { processCommonMenuItems } from 'src/app/core/services/table-menu-process.service';
import {
  processSelectAllClick,
  processSelectItemClick,
  processSelectLogic,
  processThreeDotsMenu,
} from './client-side-data-table.utils';

@UntilDestroy()
@Component({
  selector: 'app-client-side-data-table',
  templateUrl: './client-side-data-table.component.html',
})
export class ClientSideDataTableComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input()
  id!: string;
  @Input()
  columns!: DataTables.ColumnSettings[];
  @Input() selectFieldName: string = 'uniqueId';
  @Input() sort: any;
  @Input() eventService: any;
  @Input() data: any;
  @Input() paging: any = true;
  @Input() ordering: any = true;
  @Input() refreshIt: BehaviorSubject<any> | undefined;

  @ViewChild(DataTableDirective, { static: false })
  dtOptions!: DataTables.Settings;

  dtTrigger = new Subject<any>();

  selectedIds: string[] = [];
  filterItems!: SearchItemModel[];

  listenerFn: (() => void) | undefined;
  tableTranslationsData: {} | undefined;
  apiInstance: DataTables.Api | undefined;
  private isDestroyed = false;

  constructor(
    private renderer: Renderer2,
    private clientSideSearchService: ClientSideSearchService,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initTranslations();
    this.initSubscriptions();
    this.dtOptions = {
      ...this.getDTOptions(),
      ajax: (dataTablesParameters: any, callback) => {
        this.ajaxFunction(dataTablesParameters, callback);
      },
    };
  }

  ngOnDestroy() {
    this.isDestroyed = true;

    if (this.listenerFn) {
      this.listenerFn();
    }

    this.destroyDataTable();

    this.dtTrigger.unsubscribe();
    this.eventService.dataWasLoaded = false;
  }

  rerender(data?: any): void {
    if (this.isDestroyed || !this.columns?.length || !document.querySelector(this.tableSelector)) {
      return;
    }

    const that = this;
    this.saveCurrentPagination();
    this.destroyDataTable();

    if (data) {
      this.eventService.setData(data);
    }

    this.apiInstance = $(this.tableSelector).DataTable({
      ...this.getDTOptions(),
      ajax: (dataTablesParameters: any, callback) => {
        this.ajaxFunction(dataTablesParameters, callback, data);
      },
    });

    this.apiInstance.on('draw', function () {
      processSelectLogic(that.apiInstance, that.selectFieldName, that.selectedIds);
    });

    this.apiInstance.on('page', function (data) {
      processSelectLogic(that.apiInstance, that.selectFieldName, that.selectedIds);
    });
  }

  ajaxFunction(dataTablesParameters: any, callback: any, data?: any): any {
    this.eventService.dataWasLoaded = true;
    this.cdr.detectChanges();
    callback({
      data: data || this.data,
    });
  }

  getDTOptions(): any {
    return {
      destroy: true,
      paging: this.paging,
      pagingType: 'full_numbers',
      pageLength: this.getPageLength(),
      displayStart: this.getDisplayStart(),
      ordering: this.ordering,
      order: this.sort,
      serverSide: false,
      processing: false,
      stateSave: false,
      searching: false,
      columns: this.columns,
      language: this.tableTranslationsData,
      stateSaveParams: (data: any) => {
        const pagination = {
          start: data.oSavedState.start,
          length: data.oSavedState.length,
        };

        this.eventService?.savePagination(pagination);
      },
    };
  }

  getPageLength(): number {
    const preSavedPagination: Pagination = this.eventService?.getPagination();

    if (preSavedPagination?.length) {
      return preSavedPagination.length;
    }

    return constants.defaultDataTablePageLength;
  }

  getDisplayStart(): number {
    const preSavedPagination: Pagination = this.eventService?.getPagination();

    return preSavedPagination?.start || 0;
  }

  private get tableSelector(): string {
    return `#${this.id}-data-table`;
  }

  private destroyDataTable(): void {
    if ($.fn.dataTable.isDataTable(this.tableSelector)) {
      this.apiInstance?.off('draw');
      this.apiInstance?.off('page');
      $(this.tableSelector).DataTable().destroy();
      $(this.tableSelector).empty();
    }

    this.apiInstance = undefined;
  }

  ngAfterViewInit(): void {
    this.listenerFn = this.renderer.listen('document', 'click', (event) => {
      processThreeDotsMenu(event);

      if (!this.isEventInsideCurrentTable(event)) {
        return;
      }

      this.processSelectors(event);

      processCommonMenuItems(this.eventService, event);
    });

    this.dtTrigger.next(null);
  }

  processSelectors(event: any) {
    const isSelectAll = event.target.id === 'selectAll';
    const isSelectItem = event.target.id === 'selectItem';

    if (!isSelectAll && !isSelectItem) {
      return;
    }

    if (isSelectAll) {
      this.selectedIds = processSelectAllClick(
        event,
        this.selectedIds,
        this.apiInstance,
        this.selectFieldName,
      );
    }

    if (isSelectItem) {
      this.selectedIds = processSelectItemClick(event, this.selectedIds);
    }

    processSelectLogic(this.apiInstance, this.selectFieldName, this.selectedIds);
    this.eventService.onSelected(this.selectedIds);
  }

  private isEventInsideCurrentTable(event: Event): boolean {
    const target = event.target;

    if (!(target instanceof Element)) {
      return false;
    }

    return target.closest('table')?.id === `${this.id}-data-table`;
  }

  private initTranslations(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.translocoService
        .selectTranslation()
        .pipe(take(1), untilDestroyed(this))
        .subscribe((data) => {
          this.tableTranslationsData = {
            info: data['dataTableLanguageSettings.info'],
            infoEmpty: data['dataTableLanguageSettings.infoEmpty'],
            lengthMenu: data['dataTableLanguageSettings.lengthMenu'],
            zeroRecords: data['dataTableLanguageSettings.zeroRecords'],
            paginate: {
              first: data['dataTableLanguageSettings.paginate.first'],
              last: data['dataTableLanguageSettings.paginate.last'],
              next: data['dataTableLanguageSettings.paginate.next'],
              previous: data['dataTableLanguageSettings.paginate.previous'],
            },
          };
        });
      this.rerender();
    });
  }

  private initSubscriptions(): void {
    this.refreshIt?.pipe(untilDestroyed(this)).subscribe((refresh: any) => {
      if (refresh) {
        this.columns = refresh;
        this.rerender();
      }
    });

    this.eventService
      .getFilterConditionsSubject()
      .pipe(untilDestroyed(this))
      .subscribe((filterItems: SearchItemModel[]) => {
        this.filterItems = filterItems.map((item: SearchItemModel) => {
          item.searchValue = item.searchValue?.toString();
          return item;
        });

        this.processFilters();
      });

    this.eventService?.resetSelections$
      .asObservable()
      .pipe(untilDestroyed(this))
      .subscribe((reset: boolean) => {
        reset && this.resetSelections();
      });

    this.eventService?.selectAll$
      .asObservable()
      .pipe(untilDestroyed(this))
      .subscribe((select: boolean) => {
        select && this.selectAll();
      });
  }

  private saveCurrentPagination(): void {
    const datatable = this.getDataTable();

    if (!datatable) {
      return;
    }

    const pageInfo = datatable.page.info();

    this.eventService?.savePagination({
      start: pageInfo.start,
      length: pageInfo.length,
    });
  }

  private getDataTable(): DataTables.Api | null {
    if (!$.fn.dataTable.isDataTable(this.tableSelector)) {
      return null;
    }

    return $(this.tableSelector).DataTable();
  }

  private resetSelections(): void {
    if (this.isDestroyed) {
      return;
    }

    const tableElement = document.querySelector(this.tableSelector);
    const selectAllCheckbox = tableElement?.querySelector('thead input#selectAll');

    this.selectedIds = [];

    if (selectAllCheckbox) {
      (selectAllCheckbox as HTMLInputElement).indeterminate = false;
      (selectAllCheckbox as HTMLInputElement).checked = false;
    }

    $(this.tableSelector).find('input#selectItem').prop('checked', false);
    // this.eventService.onSelected(this.selectedIds);
  }

  private selectAll(): void {
    if (this.isDestroyed) {
      return;
    }

    this.selectedIds =
      this.apiInstance
        ?.data()
        .toArray()
        .map((item: any) => {
          return item[this.selectFieldName] + '';
        }) || this.selectedIds;

    processSelectLogic(this.apiInstance, this.selectFieldName, this.selectedIds);
    this.eventService.onSelected(this.selectedIds);
  }

  private processFilters(): void {
    if (this.filterItems?.length > 0) {
      let dataFiltered = Object.assign(this.data);

      this.filterItems.forEach((filterItem) => {
        const criterion = this.eventService
          ?.getFilterCriterions()
          .find((filterCriterion: Criterion) => filterItem.criterion === filterCriterion.id);

        dataFiltered = this.clientSideSearchService.doFilterItems(
          {
            propertyNames: criterion.data,
            propertyValue: filterItem.searchValue,
            operator: filterItem.operator,
          } as FilterRequestItem,
          dataFiltered,
          criterion.type,
        );
      });

      this.rerender(dataFiltered);
    } else {
      setTimeout(() => {
        if (this.isDestroyed) {
          return;
        }

        this.rerender(this.data);
      }, 1);
    }
  }
}

export class DataTableItem {
  title!: string;
  event!: string;
  icon!: string;
  disabled?: boolean = false;
  separatorAfter?: boolean = false;
}
